import { api, type ApiType } from 'lib/api';
import { stringParse } from 'utils';

/**
 * Multipart `EntityType` value for legacy `POST /api/v2.0/fileupload`.
 * Mirrors the server-side enum; the server is the source of truth for allowed values.
 */
export const LEGACY_FILE_UPLOAD_ENTITY_TYPE_TEMP = 'temp';

/**
 * EntityType value for facility certificate PDF uploads.
 * Used to identify and validate certificate-specific uploads (20MB PDF limit).
 */
export const LEGACY_FILE_UPLOAD_ENTITY_TYPE_CERTIFICATE = 'certificate';

export const apiSuppliers: ApiType = {
  get: {
    search: ({ query, vars }) => api.get(stringParse('/api/suppliers?search={0}', vars), query),
    list: () => api.get('/api/suppliers?isActive=true'),
    listItemSuppliers: () => api.get('/api/suppliers?isActive=true&isItemSupplier=true'),
    listItemSuppliersSearch: ({ query, vars }) =>
      api.get(
        stringParse('/api/suppliers?isActive=true&isItemSupplier=true&search={0}', vars),
        query
      ),
    listStyleSuppliersSearch: ({ query, vars }) =>
      api.get(
        stringParse('/api/suppliers?isActive=true&isStyleSupplier=true&search={0}', vars),
        query
      ),
    listSupplierPortalEnabled: ({ vars }) =>
      api.get(
        stringParse(
          '/api/suppliers?isActive=true&supplierPortalEnabled=true&isStyleSupplier=true&search={0}',
          vars
        )
      ),
    listActiveWithPortal: ({ query }) =>
      api.get('/api/suppliers?supplierPortalEnabled=true', query),
    byDirectSupplierId: ({ vars }) => api.get(stringParse('/api/suppliers/{0}', vars)),
    facilityProcesstype: () => api.get('/api/facilityprocesstype'),
    /** Process category reference data for PO creation (Step 4: map processes). */
    processCategories: () => api.get('/api/process-categories'),
    facilitiesList: ({ vars, query }) =>
      api.get(stringParse('/api/organizations/{0}/sub-suppliers/{1}/facilities', vars), query),
    /**
     * Full certificate rows for direct-supplier Certificates tab (flat shape per table columns).
     * `GET /api/organizations/{orgId}/suppliers/{supplierId}/certificates`
     */
    directSupplierCertificatesList: ({ vars, query }) =>
      api.get(stringParse('/api/organizations/{0}/suppliers/{1}/certificates', vars), query),
    globalSubSupplierList: ({ vars }) =>
      api.get(stringParse('/api/meta/search/global-sub-suppliers?search={0}', vars)),
    mergeRequest: ({ vars }) => api.get(stringParse('/api/suppliers/{0}/mergerequests/{1}', vars)),
    facilityCertificatesList: ({ vars }) =>
      api.get(
        stringParse('/api/organizations/{0}/sub-suppliers/{1}/facilities/{2}/certificates', vars)
      ),
  },
  post: {
    create: ({ vars, data }) =>
      api.post(stringParse('/api/organizations/{0}/organizationsubsuppliers', vars), data),
    createFacilities: ({ vars, data }) => {
      return api.post(
        stringParse('/api/organizations/{0}/sub-suppliers/{1}/facilities', vars),
        data
      );
    },
    /**
     * Multipart temp upload for certificate files.
     * Legacy-compatible endpoint returns a file resource payload (`id` used as `documentResourceId`).
     */
    facilityCertificateDocumentUpload: ({ data }) =>
      api.post('/api/v2.0/fileupload', data, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Accept: '*/*',
        },
      }),
    /** JSON: `CreateFacilityCertificateRequest` — certificate metadata (no file required). */
    facilityCertificateCreate: ({ vars, data }) =>
      api.post(
        stringParse('/api/organizations/{0}/sub-suppliers/{1}/facilities/{2}/certificates', vars),
        data
      ),
    signup: ({ data }) => api.post('/api/supplierportalaccount', data),
    mergeRequestAction: ({ vars, data }) =>
      api.post(stringParse('/api/suppliers/{0}/mergerequests/{1}', vars), data),
  },
  put: {
    updateSupplier: ({ vars, data }) => api.put(stringParse('/api/suppliers/{0}', vars), data),
    /** JSON partial update — `UpdateFacilityCertificateRequest` (certificate fields + optional document). */
    facilityCertificateUpdate: ({ vars, data }) =>
      api.put(
        stringParse(
          '/api/organizations/{0}/sub-suppliers/{1}/facilities/{2}/certificates/{3}',
          vars
        ),
        data
      ),
    byId: ({ data, vars }) =>
      api.put(stringParse('/api/organizations/{0}/sub-suppliers/{1}/facility/{2}', vars), data),
    bulkUpdate: ({ data, vars }) =>
      api.put(stringParse('/api/organizations/{0}/sub-suppliers/{1}/facility', vars), data),
    bulkDeleteFacilities: ({ vars, data }) =>
      api.put(stringParse('/api/organizations/{0}/sub-suppliers/{1}/facilities', vars), data),
    facilityCertificate: ({ data, vars }) =>
      api.put(
        stringParse(
          '/api/organizations/{0}/sub-suppliers/{1}/facilities/{2}/certificates/{3}',
          vars
        ),
        data
      ),
  },
  delete: {
    byFacilityId: ({ vars }) =>
      api.delete(stringParse('/api/organizations/{0}/sub-suppliers/{1}/facilities/{2}', vars)),
    facilityCertificate: ({ vars }) =>
      api.delete(
        stringParse(
          '/api/organizations/{0}/sub-suppliers/{1}/facilities/{2}/certificates/{3}',
          vars
        )
      ),
    /** Pending certificate file upload (before certificate metadata create). Removes server-side pending document. */
    facilityCertificateDocument: ({ vars }) =>
      api.delete(
        stringParse('/api/organizations/{0}/facilities/{1}/certificate-document/{2}', vars)
      ),
  },
};
